import { DefaultContext, useQuery } from '@apollo/client'
import React, { FC, useContext, useMemo } from 'react'
import styled from 'styled-components'

import { RewardsDocument, RewardsQuery } from '../../../../../graphql/search'
import useLogAndCaptureError from '../../../../../hooks/useLogAndCaptureError'
import { ApolloClientsContext } from '../../../../../lib/graphql/apollo'
import { parseRewardsSnapshots } from '../../../../../lib/rewards/snapshots'
import { Breadcrumb, BreadcrumbPart } from '../../../../global/Breadcrumb'
import Spinner from '../../../../Spinner'
import QueryFallback from '../../QueryFallback'
import { RewardsHeading2 } from '../RewardsHeading'
import { CommissionsCard } from './commissions/CommissionsCard'
import { RebatesCard } from './rebates/RebatesCard'
import { TotalRewardsCard } from './TotalRewardsCard'

export interface RewardsDetailsPayoutsContentProps {
  data: RewardsQuery
  refetchBalance: () => Promise<void>
}

export const Page = styled.div`
  padding-bottom: 30px;
`

export const Heading2 = styled(RewardsHeading2)`
  font-size: 22px;
  margin-top: 40px;
`

const breadcrumb: BreadcrumbPart[] = [
  {
    label: 'Account Dashboard',
    path: '/account',
  },
  {
    label: 'Details and Payout',
    path: '/account/rewards/details-payouts',
  },
]

const RewardsDetailsPayoutsContent: FC<RewardsDetailsPayoutsContentProps> = ({
  data,
  refetchBalance,
}) => {
  const availableBalance = data.customer?.availableBalance
  const rewardsSnapshots = data.customer?.rewardsSnapshots?.nodes ?? []
  const [, previousSnapshot] = parseRewardsSnapshots(rewardsSnapshots)
  const availableBalanceDefaulted = useMemo(() => {
    if (!availableBalance) {
      return { commissionsBalance: 0, rebateBalance: 0, storeCredit: 0, totalBalance: 0 }
    }
    return {
      commissionsBalance: parseFloat(availableBalance.commissionsBalance.amount),
      rebateBalance: parseFloat(availableBalance.rebateBalance.amount),
      storeCredit: parseFloat(availableBalance.storeCredit.amount),
      totalBalance: parseFloat(availableBalance.totalBalance.amount),
    }
  }, [availableBalance])
  return (
    <Page>
      <Breadcrumb breadcrumb={breadcrumb} />
      <Heading2>Details and Payouts</Heading2>
      <TotalRewardsCard
        availableBalance={availableBalanceDefaulted}
        previousSnapshot={previousSnapshot}
      />
      <CommissionsCard
        availableBalance={availableBalanceDefaulted}
        refetchBalance={refetchBalance}
      />
      <RebatesCard availableBalance={availableBalanceDefaulted} refetchBalance={refetchBalance} />
    </Page>
  )
}

export const RewardsDetailsPayouts: FC = () => {
  const { comma: commaClient } = useContext(ApolloClientsContext)
  const { data, error, loading, refetch } = useQuery(RewardsDocument, {
    client: commaClient,
    context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
  })
  useLogAndCaptureError(error)
  if (loading) {
    return <Spinner loading />
  }
  if (error) {
    return (
      <QueryFallback buttonText="Retry Loading Rewards" error={error} onRetry={() => refetch()} />
    )
  }
  if (!data) {
    return null
  }
  return (
    <RewardsDetailsPayoutsContent
      data={data}
      refetchBalance={async () => {
        await refetch()
      }}
    />
  )
}
